"use client";

import { c as _c } from "react/compiler-runtime";
import { Tabs as HeroTabs } from "@heroui/react";
import type React from "react";
import { useEffect, useMemo } from "react";
import { useScrollShadow } from "../../hooks/useScrollShadow/useScrollShadow";
import { cn, mergeRefs } from "../../lib/utils";
import styles from "./Tabs.module.css";
export type TabsProps = React.ComponentProps<typeof HeroTabs> & {
  className?: string;
  classNames?: React.ComponentProps<typeof HeroTabs>["classNames"];
};

// An inset box-shadow to indicate to the user that they can scroll.
function getScrollShadowClass(canScrollLeft: boolean, canScrollRight: boolean) {
  if (canScrollLeft && canScrollRight) {
    return "shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.2),inset_-10px_0_8px_-8px_rgba(0,0,0,0.2)] dark:shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.45),inset_-10px_0_8px_-8px_rgba(0,0,0,0.45)]";
  }
  if (canScrollLeft) {
    return "shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.2)] dark:shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.45)]";
  }
  if (canScrollRight) {
    return "shadow-[inset_-10px_0_8px_-8px_rgba(0,0,0,0.2)] dark:shadow-[inset_-10px_0_8px_-8px_rgba(0,0,0,0.45)]";
  }
  return undefined;
}

/**
 * The selected-tab indicator is a sibling of the tabs that HeroUI positions with inline
 * left/top/width/height, measured once in a ref callback. Nothing re-measures it, so a tab list that
 * changes width — a resized window, an opening panel — keeps an indicator sized for the old layout.
 * Mirrors HeroUI's own geometry, including the inset it gives the underlined variant.
 */
function positionCursor(tabList: HTMLElement, variant: TabsProps["variant"]) {
  const cursor = tabList.querySelector<HTMLElement>('[data-slot="cursor"]');
  const selected = tabList.querySelector<HTMLElement>('[data-slot="tab"][data-selected="true"]');
  if (!cursor || !selected) return;
  const {
    offsetLeft,
    offsetTop,
    offsetWidth,
    offsetHeight
  } = selected;
  if (variant === "underlined") {
    cursor.style.left = `${offsetLeft + offsetWidth * 0.1}px`;
    cursor.style.top = `${offsetTop + offsetHeight - 2}px`;
    cursor.style.width = `${offsetWidth * 0.8}px`;
    return;
  }
  cursor.style.left = `${offsetLeft}px`;
  cursor.style.top = `${offsetTop}px`;
  cursor.style.width = `${offsetWidth}px`;
  cursor.style.height = `${offsetHeight}px`;
}

// Create the wrapper component
const TabsWrapper = t0 => {
  const $ = _c(41);
  let children;
  let className;
  let classNames;
  let props;
  let ref;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      classNames,
      color: t1,
      variant,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = classNames;
    $[4] = props;
    $[5] = ref;
    $[6] = t1;
    $[7] = variant;
  } else {
    children = $[1];
    className = $[2];
    classNames = $[3];
    props = $[4];
    ref = $[5];
    t1 = $[6];
    variant = $[7];
  }
  const color = t1 === undefined ? "default" : t1;
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      autoDetect: false
    };
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    containerRef: tabListRef,
    canScrollLeft,
    canScrollRight
  } = useScrollShadow(t2);
  let t3;
  if ($[9] !== ref || $[10] !== tabListRef) {
    t3 = mergeRefs(tabListRef, ref);
    $[9] = ref;
    $[10] = tabListRef;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const mergedRef = t3;
  let t4;
  let t5;
  if ($[12] !== tabListRef || $[13] !== variant) {
    t4 = () => {
      const tabList = tabListRef.current;
      if (!tabList || typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(() => positionCursor(tabList, variant));
      observer.observe(tabList);
      for (const tab of tabList.querySelectorAll("[data-slot=\"tab\"]")) {
        observer.observe(tab);
      }
      return () => observer.disconnect();
    };
    t5 = [tabListRef, variant];
    $[12] = tabListRef;
    $[13] = variant;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  const t6 = color === "default" && styles.tabsDefault;
  const t7 = color === "primary" && styles.tabsPrimary;
  const t8 = color === "secondary" && styles.tabsSecondary;
  const t9 = color === "success" && styles.tabsSuccess;
  const t10 = color === "warning" && styles.tabsWarning;
  const t11 = color === "danger" && styles.tabsDanger;
  const t12 = variant === "underlined" && styles.tabsUnderlined;
  const t13 = classNames?.base;
  let t14;
  if ($[16] !== className || $[17] !== t10 || $[18] !== t11 || $[19] !== t12 || $[20] !== t13 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
    t14 = cn("min-w-0 max-w-full", t6, t7, t8, t9, t10, t11, t12, t13, className);
    $[16] = className;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t13;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== canScrollLeft || $[27] !== canScrollRight || $[28] !== classNames?.tabList) {
    t15 = cn("overflow-x-auto transition-shadow duration-200", getScrollShadowClass(canScrollLeft, canScrollRight), classNames?.tabList);
    $[26] = canScrollLeft;
    $[27] = canScrollRight;
    $[28] = classNames?.tabList;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== classNames || $[31] !== t14 || $[32] !== t15) {
    t16 = {
      ...classNames,
      base: t14,
      tabList: t15
    };
    $[30] = classNames;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== children || $[35] !== color || $[36] !== mergedRef || $[37] !== props || $[38] !== t16 || $[39] !== variant) {
    t17 = <HeroTabs {...props} ref={mergedRef} color={color} variant={variant} classNames={t16}>{children}</HeroTabs>;
    $[34] = children;
    $[35] = color;
    $[36] = mergedRef;
    $[37] = props;
    $[38] = t16;
    $[39] = variant;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
};

// Copy all static properties from the original component
Object.setPrototypeOf(TabsWrapper, HeroTabs);
Object.assign(TabsWrapper, HeroTabs);

// Ensure the component has the right displayName and type
TabsWrapper.displayName = (HeroTabs as React.ComponentType<unknown>).displayName || "Tabs";

// Copy the $$typeof symbol if it exists
if ("$$typeof" in HeroTabs) {
  (TabsWrapper as unknown as Record<string, unknown>).$$typeof = (HeroTabs as unknown as Record<string, unknown>).$$typeof;
}
export const Tabs = TabsWrapper;